<template>
  <div class="grc-dashboard-module animated fadeIn">
    <b-alert v-if="shouldSelectCompany" variant="info" show>
      Selecione uma empresa para visualizar o Dashboard GRC.
    </b-alert>

    <template v-else>
      <b-overlay :show="isLoading" rounded="sm" opacity="0.6" spinner-small>
        <b-card class="metrics-container mb-4">
          <div
            class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3"
          >
            <div>
              <h4 class="mb-1">Painel GRC</h4>
              <p class="text-muted mb-0">
                Visão consolidada de requisitos, riscos, controles e planos de
                ação.
              </p>
            </div>
            <div class="d-flex align-items-center flex-wrap gap-2">
              <small v-if="analyticsScope.aggregate" class="text-muted">
                Consolidado de {{ analyticsScope.totalCompanies }} empresas
              </small>
              <div
                v-if="!analyticsScope.aggregate && areas.length"
                class="d-flex align-items-center"
              >
                <label class="mb-0 mr-2 text-muted small">
                  Filtrar por área:
                </label>
                <b-form-select
                  v-model="selectedAreaId"
                  size="sm"
                  class="grc-area-filter"
                  :options="areaOptions"
                />
              </div>
            </div>
          </div>
          <div class="metrics-grid">
            <div
              v-for="metric in metricsCards"
              :key="metric.key"
              class="metric-section"
              :class="{
                'metric-section-clickable':
                  metric.clickable && canOpenModuleOverview,
              }"
              @click="openMetric(metric.key)"
            >
              <div class="metric-header">
                <div class="metric-icon" :class="metric.iconClass">
                  <i :class="metric.icon"></i>
                </div>
                <p class="metric-label">{{ metric.label }}</p>
              </div>
              <h3 class="metric-value">{{ metric.value }}</h3>
              <p class="metric-caption mb-0">{{ metric.caption }}</p>
              <div v-if="metric.trend" class="metric-trend">
                <span
                  class="trend-badge"
                  :class="trendBadgeClass(metric.trend.delta)"
                >
                  <i :class="trendIcon(metric.trend.delta)"></i>
                  {{ formatTrendDelta(metric.trend.delta) }}
                </span>
                <span class="trend-caption">
                  novos em {{ analyticsScope.windowDays || 30 }} dias
                </span>
              </div>
              <div v-if="metric.alert" class="metric-alert">
                <i class="fas fa-exclamation-circle mr-1"></i>
                {{ metric.alert }}
              </div>
            </div>
          </div>
        </b-card>

        <b-row>
          <b-col
            v-for="donut in statusDonuts"
            :key="donut.key"
            md="6"
            lg="3"
            class="mb-3"
          >
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <h6 class="card-title mb-2 donut-title">
                  <i :class="donut.icon" class="mr-2"></i>
                  {{ donut.title }}
                </h6>
                <ModernPieChart
                  :series="donut.series"
                  :labels="donut.labels"
                  :colors="donut.colors"
                  :height="220"
                  :show-donut="true"
                  :show-data-labels="false"
                  :show-legend="true"
                />
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <b-col lg="7" class="mb-3">
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <div
                  class="d-flex align-items-center justify-content-between flex-wrap mb-3 gap-2"
                >
                  <div>
                    <h5 class="card-title mb-0">
                      <i class="fas fa-tasks mr-2"></i>
                      Planos prioritários
                    </h5>
                    <small class="text-muted">
                      Ações críticas em andamento para o recorte atual.
                    </small>
                  </div>
                  <div class="quick-links">
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      :disabled="!canOpenModuleOverview"
                      @click="openRoute('/grc/requisitos', buildRouteQuery())"
                    >
                      Requisitos
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      :disabled="!canOpenModuleOverview"
                      @click="openRoute('/grc/riscos', buildRouteQuery())"
                    >
                      Riscos
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      :disabled="!canOpenModuleOverview"
                      @click="openRoute('/grc/controles', buildRouteQuery())"
                    >
                      Controles
                    </b-button>
                    <b-button
                      size="sm"
                      variant="primary"
                      :disabled="!canOpenModuleOverview"
                      @click="openRoute('/grc/planos-acao', buildRouteQuery())"
                    >
                      Planos de Ação
                    </b-button>
                  </div>
                </div>
                <b-table
                  small
                  hover
                  responsive="sm"
                  :items="priorityPlans"
                  :fields="actionPlanFields"
                  empty-text="Sem planos de ação prioritários para exibir"
                  class="mb-0 grc-table"
                  :tbody-tr-class="rowClassByOverdue"
                  @row-clicked="openPriorityPlan"
                >
                  <template #cell(companyName)="{ item }">
                    {{ item.companyName || "-" }}
                  </template>
                  <template #cell(priority)="{ item }">
                    <b-badge :variant="priorityVariant(item.priority)">
                      {{ priorityLabel(item.priority) }}
                    </b-badge>
                  </template>
                  <template #cell(progress)="{ item }">
                    {{ item.progress || 0 }}%
                  </template>
                  <template #cell(status)="{ item }">
                    <b-badge :variant="actionPlanStatusVariant(item.status)">
                      {{ actionPlanStatusLabel(item.status) }}
                    </b-badge>
                  </template>
                  <template #cell(dueDate)="{ item }">
                    <span
                      :class="{
                        'text-danger font-weight-bold': item.isOverdue,
                      }"
                    >
                      {{ formatDate(item.dueDate) }}
                    </span>
                    <b-badge
                      v-if="item.isOverdue"
                      variant="danger"
                      class="ml-1"
                    >
                      {{ item.daysOverdue }}d atraso
                    </b-badge>
                  </template>
                </b-table>
              </b-card-body>
            </b-card>
          </b-col>

          <b-col lg="5" class="mb-3">
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <h5 class="card-title mb-3">
                  <i class="fas fa-balance-scale mr-2"></i>
                  Requisitos em foco
                </h5>
                <b-table
                  small
                  hover
                  responsive="sm"
                  :items="requirementHighlights"
                  :fields="requirementFields"
                  empty-text="Sem requisitos para o recorte atual"
                  class="mb-0 grc-table"
                  :tbody-tr-class="rowClassByOverdue"
                  @row-clicked="openRequirementHighlight"
                >
                  <template #cell(companyName)="{ item }">
                    {{ item.companyName || "-" }}
                  </template>
                  <template #cell(status)="{ item }">
                    <b-badge :variant="requirementStatusVariant(item.status)">
                      {{ requirementStatusLabel(item.status) }}
                    </b-badge>
                  </template>
                  <template #cell(nextReviewAt)="{ item }">
                    <span
                      :class="{
                        'text-danger font-weight-bold': item.isOverdue,
                      }"
                    >
                      {{ formatDate(item.nextReviewAt) }}
                    </span>
                    <b-badge
                      v-if="item.isOverdue"
                      variant="danger"
                      class="ml-1"
                    >
                      Vencido
                    </b-badge>
                  </template>
                </b-table>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <b-col lg="6" class="mb-3" v-if="analyticsScope.aggregate">
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <h5 class="card-title mb-3">
                  <i class="fas fa-building mr-2"></i>
                  Panorama por empresa
                </h5>
                <b-table
                  small
                  hover
                  responsive="sm"
                  :items="companyBreakdown"
                  :fields="companyBreakdownFields"
                  empty-text="Sem dados por empresa para o recorte atual"
                  class="mb-0 grc-table"
                  @row-clicked="openCompanyBreakdown"
                />
              </b-card-body>
            </b-card>
          </b-col>

          <b-col :lg="analyticsScope.aggregate ? 6 : 12" class="mb-3">
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <h5 class="card-title mb-3">
                  <i class="fas fa-layer-group mr-2"></i>
                  Panorama por área
                </h5>
                <b-table
                  small
                  hover
                  responsive="sm"
                  :items="areaBreakdown"
                  :fields="areaBreakdownFields"
                  empty-text="Sem dados por área para o recorte atual"
                  class="mb-0 grc-table"
                  @row-clicked="openAreaBreakdown"
                >
                  <template #cell(companyName)="{ item }">
                    {{ item.companyName || "-" }}
                  </template>
                </b-table>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <b-col lg="5" class="mb-3">
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <h5 class="card-title mb-3">
                  <i class="fas fa-chart-pie mr-2"></i>
                  Estratégia de tratamento dos riscos
                </h5>
                <ModernPieChart
                  :series="treatmentSeries"
                  :labels="treatmentLabels"
                  :height="320"
                  :show-donut="true"
                />
              </b-card-body>
            </b-card>
          </b-col>

          <b-col lg="7" class="mb-3">
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <div
                  class="d-flex align-items-center justify-content-between flex-wrap mb-3 gap-2"
                >
                  <h5 class="card-title mb-0">
                    <i class="fas fa-arrow-trend-up mr-2"></i>
                    Top 10 maiores riscos
                  </h5>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    :disabled="!canOpenModuleOverview"
                    @click="
                      openRoute(
                        '/grc/riscos',
                        buildRouteQuery({ statuses: activeRiskStatuses })
                      )
                    "
                  >
                    Abrir lista de riscos
                  </b-button>
                </div>
                <b-table
                  small
                  hover
                  responsive="sm"
                  :items="topRisks"
                  :fields="topRiskFields"
                  empty-text="Sem riscos ativos para o recorte atual"
                  class="mb-0 grc-table"
                  @row-clicked="openTopRisk"
                >
                  <template #cell(companyName)="{ item }">
                    {{ item.companyName || "-" }}
                  </template>
                  <template #cell(inherentLevel)="{ item }">
                    <b-badge :variant="riskLevelVariant(item.inherentLevel)">
                      {{ riskLevelLabel(item.inherentLevel) }}
                    </b-badge>
                  </template>
                  <template #cell(treatmentStrategy)="{ item }">
                    {{ treatmentStrategyLabel(item.treatmentStrategy) }}
                  </template>
                  <template #cell(nextReviewAt)="{ item }">
                    {{ formatDate(item.nextReviewAt) }}
                  </template>
                </b-table>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" class="mb-3">
            <b-card no-body class="dashboard-card chart-animation">
              <b-card-body>
                <div
                  class="d-flex align-items-center justify-content-between flex-wrap mb-3 gap-2"
                >
                  <h5 class="card-title mb-0">
                    <i class="fas fa-calendar-check mr-2"></i>
                    Riscos vencidos ou a vencer
                  </h5>
                  <small class="text-muted">
                    Revisões vencidas ou vencendo em até
                    {{ reviewAlertWindowDays }} dias
                  </small>
                </div>
                <b-table
                  small
                  hover
                  responsive="sm"
                  :items="reviewAlerts"
                  :fields="reviewAlertFields"
                  empty-text="Sem revisões críticas para o recorte atual"
                  class="mb-0 grc-table"
                  @row-clicked="openReviewAlert"
                >
                  <template #cell(companyName)="{ item }">
                    {{ item.companyName || "-" }}
                  </template>
                  <template #cell(inherentLevel)="{ item }">
                    <b-badge :variant="riskLevelVariant(item.inherentLevel)">
                      {{ riskLevelLabel(item.inherentLevel) }}
                    </b-badge>
                  </template>
                  <template #cell(nextReviewAt)="{ item }">
                    <span
                      :class="
                        item.isOverdue ? 'text-danger font-weight-bold' : ''
                      "
                    >
                      {{ formatDate(item.nextReviewAt) }}
                    </span>
                  </template>
                  <template #cell(reviewStatus)="{ item }">
                    <b-badge :variant="item.isOverdue ? 'danger' : 'warning'">
                      {{ item.isOverdue ? "Vencido" : "A vencer" }}
                    </b-badge>
                  </template>
                </b-table>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </b-overlay>
    </template>
  </div>
</template>

<script>
import { getGrcDashboardAnalytics } from "@/api/grc-dashboard";
import ModernBarChart from "../charts/ModernBarChart.vue";
import ModernPieChart from "../charts/ModernPieChart.vue";

const RISK_STATUS = {
  OPEN: 1,
  WAITING_APPROVAL: 2,
  CLOSED: 3,
  IN_REVIEW: 4,
};

const REQUIREMENT_STATUS = {
  NOT_ATTENDED: 1,
  PARTIALLY_ATTENDED: 2,
  ATTENDED: 3,
  NOT_APPLICABLE: 4,
};

const ACTION_PLAN_STATUS = {
  PLANNED: 1,
  IN_PROGRESS: 2,
  COMPLETED: 3,
  WAITING_APPROVAL: 4,
  SUSPENDED: 5,
};

const CONTROL_STATUS = {
  NOT_CONTROLLED: 1,
  PARTIALLY_CONTROLLED: 2,
  CONTROLLED: 3,
  IN_REVIEW: 4,
};

const REVIEW_ALERT_WINDOW_DAYS = 15;

const REQUIREMENT_STATUS_META = {
  [REQUIREMENT_STATUS.NOT_ATTENDED]: {
    label: "Não atendido",
    color: "#ef4444",
  },
  [REQUIREMENT_STATUS.PARTIALLY_ATTENDED]: {
    label: "Parcial",
    color: "#f59e0b",
  },
  [REQUIREMENT_STATUS.ATTENDED]: { label: "Atendido", color: "#10b981" },
  [REQUIREMENT_STATUS.NOT_APPLICABLE]: {
    label: "Não aplicável",
    color: "#94a3b8",
  },
};

const RISK_STATUS_META = {
  [RISK_STATUS.OPEN]: { label: "Aberto", color: "#ef4444" },
  [RISK_STATUS.IN_REVIEW]: { label: "Em revisão", color: "#f59e0b" },
  [RISK_STATUS.WAITING_APPROVAL]: {
    label: "Aguardando aprovação",
    color: "#6366f1",
  },
  [RISK_STATUS.CLOSED]: { label: "Encerrado", color: "#10b981" },
};

const CONTROL_STATUS_META = {
  [CONTROL_STATUS.NOT_CONTROLLED]: {
    label: "Não controlado",
    color: "#ef4444",
  },
  [CONTROL_STATUS.PARTIALLY_CONTROLLED]: {
    label: "Parcial",
    color: "#f59e0b",
  },
  [CONTROL_STATUS.CONTROLLED]: { label: "Controlado", color: "#10b981" },
  [CONTROL_STATUS.IN_REVIEW]: { label: "Em revisão", color: "#6366f1" },
};

const ACTION_PLAN_STATUS_META = {
  [ACTION_PLAN_STATUS.PLANNED]: { label: "Planejado", color: "#94a3b8" },
  [ACTION_PLAN_STATUS.IN_PROGRESS]: { label: "Em execução", color: "#3b82f6" },
  [ACTION_PLAN_STATUS.WAITING_APPROVAL]: {
    label: "Aguardando aprovação",
    color: "#f59e0b",
  },
  [ACTION_PLAN_STATUS.COMPLETED]: { label: "Concluído", color: "#10b981" },
  [ACTION_PLAN_STATUS.SUSPENDED]: { label: "Suspenso", color: "#ef4444" },
};

function buildDonut(entries, meta) {
  const sorted = (entries || [])
    .slice()
    .filter((entry) => meta[entry.status])
    .sort((a, b) => Number(a.status) - Number(b.status));
  return {
    series: sorted.map((entry) => entry.total),
    labels: sorted.map((entry) => meta[entry.status].label),
    colors: sorted.map((entry) => meta[entry.status].color),
  };
}

export default {
  name: "GrcDashboard",
  components: {
    ModernBarChart,
    ModernPieChart,
  },
  props: {
    company: {
      type: Object,
      default: () => ({}),
    },
    filterCompanyId: {
      type: [Number, String],
      default: null,
    },
    allowAggregate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      selectedAreaId: null,
      analyticsScope: {
        aggregate: false,
        totalCompanies: 0,
        windowDays: 30,
        areaFilter: null,
      },
      metrics: {
        totalRequirements: 0,
        activeRisks: 0,
        controlGaps: 0,
        criticalActionPlans: 0,
        overduePlans: 0,
        overdueRequirements: 0,
      },
      trend: {
        requirements: { current: 0, previous: 0, delta: 0 },
        risks: { current: 0, previous: 0, delta: 0 },
        controls: { current: 0, previous: 0, delta: 0 },
        actionPlans: { current: 0, previous: 0, delta: 0 },
      },
      statusDistribution: {
        requirements: [],
        risks: [],
        controls: [],
        actionPlans: [],
      },
      companyBreakdown: [],
      areaBreakdown: [],
      priorityPlans: [],
      requirementHighlights: [],
      riskAnalytics: {
        treatmentDistribution: [],
        sourceDistribution: [],
        areaConcentration: [],
        topRisks: [],
        reviewAlerts: [],
      },
      areas: [],
    };
  },
  computed: {
    isAggregateMode() {
      return (
        this.allowAggregate &&
        (this.filterCompanyId === null ||
          this.filterCompanyId === undefined ||
          this.filterCompanyId === "")
      );
    },
    effectiveCompanyId() {
      if (
        this.filterCompanyId !== null &&
        this.filterCompanyId !== undefined &&
        this.filterCompanyId !== ""
      ) {
        return parseInt(this.filterCompanyId);
      }

      if (this.isAggregateMode) {
        return null;
      }

      if (this.company && this.company._id) {
        return parseInt(this.company._id);
      }

      return null;
    },
    shouldSelectCompany() {
      return !this.isAggregateMode && !this.effectiveCompanyId;
    },
    canOpenModuleOverview() {
      return Boolean(this.effectiveCompanyId);
    },
    activeRiskStatuses() {
      return [RISK_STATUS.OPEN, RISK_STATUS.IN_REVIEW];
    },
    reviewAlertWindowDays() {
      return REVIEW_ALERT_WINDOW_DAYS;
    },
    areaOptions() {
      return [
        { value: null, text: "Todas as áreas" },
        ...this.areas.map((area) => ({ value: area._id, text: area.name })),
      ];
    },
    metricsCards() {
      const overduePlansAlert =
        this.metrics.overduePlans > 0
          ? `${this.metrics.overduePlans} plano(s) com prazo vencido`
          : null;
      const overdueReqAlert =
        this.metrics.overdueRequirements > 0
          ? `${this.metrics.overdueRequirements} com revisão vencida`
          : null;

      return [
        {
          key: "requirements",
          label: "Requisitos ativos",
          value: this.metrics.totalRequirements,
          caption: "Base normativa e obrigações mapeadas",
          icon: "fas fa-book-open",
          iconClass: "metric-icon--blue",
          clickable: true,
          trend: this.trend.requirements,
          alert: overdueReqAlert,
        },
        {
          key: "risks",
          label: "Riscos ativos",
          value: this.metrics.activeRisks,
          caption: "Abertos ou em revisão",
          icon: "fas fa-exclamation-triangle",
          iconClass: "metric-icon--amber",
          clickable: true,
          trend: this.trend.risks,
        },
        {
          key: "controls",
          label: "Controles com lacuna",
          value: this.metrics.controlGaps,
          caption: "Não controlados ou parcialmente controlados",
          icon: "fas fa-shield-alt",
          iconClass: "metric-icon--rose",
          clickable: true,
          trend: this.trend.controls,
        },
        {
          key: "plans",
          label: "Planos críticos",
          value: this.metrics.criticalActionPlans,
          caption: "Em andamento ou aguardando aprovação",
          icon: "fas fa-clipboard-check",
          iconClass: "metric-icon--violet",
          clickable: true,
          trend: this.trend.actionPlans,
          alert: overduePlansAlert,
        },
        {
          key: "overdue",
          label: "Planos atrasados",
          value: this.metrics.overduePlans,
          caption: "Em aberto com prazo vencido",
          icon: "fas fa-hourglass-end",
          iconClass: "metric-icon--red",
          clickable: true,
        },
      ];
    },
    statusDonuts() {
      return [
        {
          key: "requirements",
          title: "Requisitos por status",
          icon: "fas fa-book-open",
          ...buildDonut(
            this.statusDistribution.requirements,
            REQUIREMENT_STATUS_META
          ),
        },
        {
          key: "risks",
          title: "Riscos por status",
          icon: "fas fa-exclamation-triangle",
          ...buildDonut(this.statusDistribution.risks, RISK_STATUS_META),
        },
        {
          key: "controls",
          title: "Controles por status",
          icon: "fas fa-shield-alt",
          ...buildDonut(this.statusDistribution.controls, CONTROL_STATUS_META),
        },
        {
          key: "actionPlans",
          title: "Planos por status",
          icon: "fas fa-tasks",
          ...buildDonut(
            this.statusDistribution.actionPlans,
            ACTION_PLAN_STATUS_META
          ),
        },
      ];
    },
    treatmentLabels() {
      return this.riskAnalytics.treatmentDistribution.map((item) => item.label);
    },
    treatmentSeries() {
      return this.riskAnalytics.treatmentDistribution.map((item) => item.value);
    },
    areaConcentrationCategories() {
      return this.riskAnalytics.areaConcentration.map((item) => item.areaName);
    },
    areaConcentrationSeries() {
      return [
        {
          name: "Score total",
          data: this.riskAnalytics.areaConcentration.map(
            (item) => item.totalScore
          ),
        },
      ];
    },
    sourceDistributionCategories() {
      return this.riskAnalytics.sourceDistribution.map((item) => item.label);
    },
    sourceDistributionSeries() {
      return [
        {
          name: "Riscos",
          data: this.riskAnalytics.sourceDistribution.map((item) => item.value),
        },
      ];
    },
    topRisks() {
      return this.riskAnalytics.topRisks || [];
    },
    reviewAlerts() {
      return this.riskAnalytics.reviewAlerts || [];
    },
    actionPlanFields() {
      const fields = [];
      if (this.analyticsScope.aggregate) {
        fields.push({ key: "companyName", label: "Empresa" });
      }
      fields.push(
        { key: "title", label: "Plano" },
        { key: "priority", label: "Prioridade" },
        { key: "progress", label: "Progresso", class: "text-center" },
        { key: "status", label: "Status" },
        { key: "dueDate", label: "Prazo" }
      );
      return fields;
    },
    requirementFields() {
      const fields = [];
      if (this.analyticsScope.aggregate) {
        fields.push({ key: "companyName", label: "Empresa" });
      }
      fields.push(
        { key: "title", label: "Requisito" },
        { key: "status", label: "Status" },
        { key: "nextReviewAt", label: "Próxima revisão" }
      );
      return fields;
    },
    companyBreakdownFields() {
      return [
        { key: "companyName", label: "Empresa" },
        { key: "totalRequirements", label: "Requisitos", class: "text-center" },
        { key: "activeRisks", label: "Riscos", class: "text-center" },
        { key: "controlGaps", label: "Lacunas", class: "text-center" },
        { key: "criticalActionPlans", label: "Planos", class: "text-center" },
      ];
    },
    areaBreakdownFields() {
      const fields = [];
      if (this.analyticsScope.aggregate) {
        fields.push({ key: "companyName", label: "Empresa" });
      }
      fields.push(
        { key: "areaName", label: "Área" },
        { key: "totalRequirements", label: "Requisitos", class: "text-center" },
        { key: "activeRisks", label: "Riscos", class: "text-center" },
        { key: "controlGaps", label: "Lacunas", class: "text-center" },
        { key: "criticalActionPlans", label: "Planos", class: "text-center" }
      );
      return fields;
    },
    topRiskFields() {
      const fields = [];
      if (this.analyticsScope.aggregate) {
        fields.push({ key: "companyName", label: "Empresa" });
      }
      fields.push(
        { key: "title", label: "Risco" },
        { key: "areaName", label: "Área" },
        { key: "score", label: "Score", class: "text-center" },
        { key: "inherentLevel", label: "Nível" },
        { key: "ownerName", label: "Responsável" },
        { key: "treatmentStrategy", label: "Tratamento" },
        { key: "nextReviewAt", label: "Próxima revisão" }
      );
      return fields;
    },
    reviewAlertFields() {
      const fields = [];
      if (this.analyticsScope.aggregate) {
        fields.push({ key: "companyName", label: "Empresa" });
      }
      fields.push(
        { key: "title", label: "Risco" },
        { key: "areaName", label: "Área" },
        { key: "score", label: "Score", class: "text-center" },
        { key: "inherentLevel", label: "Nível" },
        { key: "ownerName", label: "Responsável" },
        { key: "nextReviewAt", label: "Próxima revisão" },
        { key: "reviewStatus", label: "Situação" }
      );
      return fields;
    },
  },
  watch: {
    effectiveCompanyId: {
      immediate: true,
      handler() {
        this.selectedAreaId = null;
        this.loadDashboard();
      },
    },
    selectedAreaId() {
      this.loadDashboard();
    },
  },
  methods: {
    async loadDashboard() {
      if (this.shouldSelectCompany) {
        this.resetState();
        return;
      }

      this.isLoading = true;

      try {
        const params = {};
        if (this.effectiveCompanyId) {
          params.company = this.effectiveCompanyId;
        }
        if (this.selectedAreaId) {
          params.area = this.selectedAreaId;
        }

        const response = await getGrcDashboardAnalytics(params);
        const data = response.data || {};

        this.analyticsScope = {
          aggregate: data.scope?.aggregate === true,
          totalCompanies: data.scope?.totalCompanies || 0,
          windowDays: data.scope?.windowDays || 30,
          areaFilter: data.scope?.areaFilter || null,
        };

        this.metrics = {
          totalRequirements: data.metrics?.totalRequirements || 0,
          activeRisks: data.metrics?.activeRisks || 0,
          controlGaps: data.metrics?.controlGaps || 0,
          criticalActionPlans: data.metrics?.criticalActionPlans || 0,
          overduePlans: data.metrics?.overduePlans || 0,
          overdueRequirements: data.metrics?.overdueRequirements || 0,
        };

        this.trend = {
          requirements: data.trend?.requirements || {
            current: 0,
            previous: 0,
            delta: 0,
          },
          risks: data.trend?.risks || { current: 0, previous: 0, delta: 0 },
          controls: data.trend?.controls || {
            current: 0,
            previous: 0,
            delta: 0,
          },
          actionPlans: data.trend?.actionPlans || {
            current: 0,
            previous: 0,
            delta: 0,
          },
        };

        this.statusDistribution = {
          requirements: data.statusDistribution?.requirements || [],
          risks: data.statusDistribution?.risks || [],
          controls: data.statusDistribution?.controls || [],
          actionPlans: data.statusDistribution?.actionPlans || [],
        };

        this.companyBreakdown = data.companyBreakdown || [];
        this.areaBreakdown = data.areaBreakdown || [];
        this.priorityPlans = data.priorityPlans || [];
        this.requirementHighlights = data.requirementHighlights || [];

        this.riskAnalytics = {
          treatmentDistribution:
            data.riskAnalytics?.treatmentDistribution || [],
          sourceDistribution: data.riskAnalytics?.sourceDistribution || [],
          areaConcentration: data.riskAnalytics?.areaConcentration || [],
          topRisks: data.riskAnalytics?.topRisks || [],
          reviewAlerts: data.riskAnalytics?.reviewAlerts || [],
        };

        this.areas = data.areas || [];
      } catch (error) {
        const message =
          error?.response?.data ||
          error?.response?.statusText ||
          "Não foi possível carregar o dashboard GRC";
        this.$bvToast.toast(message, {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
        });
      } finally {
        this.isLoading = false;
      }
    },
    resetState() {
      this.analyticsScope = {
        aggregate: false,
        totalCompanies: 0,
        windowDays: 30,
        areaFilter: null,
      };
      this.metrics = {
        totalRequirements: 0,
        activeRisks: 0,
        controlGaps: 0,
        criticalActionPlans: 0,
        overduePlans: 0,
        overdueRequirements: 0,
      };
      this.trend = {
        requirements: { current: 0, previous: 0, delta: 0 },
        risks: { current: 0, previous: 0, delta: 0 },
        controls: { current: 0, previous: 0, delta: 0 },
        actionPlans: { current: 0, previous: 0, delta: 0 },
      };
      this.statusDistribution = {
        requirements: [],
        risks: [],
        controls: [],
        actionPlans: [],
      };
      this.companyBreakdown = [];
      this.areaBreakdown = [];
      this.priorityPlans = [];
      this.requirementHighlights = [];
      this.riskAnalytics = {
        treatmentDistribution: [],
        sourceDistribution: [],
        areaConcentration: [],
        topRisks: [],
        reviewAlerts: [],
      };
      this.areas = [];
    },
    rowClassByOverdue(item) {
      return item && item.isOverdue ? "grc-row-overdue" : "";
    },
    trendIcon(delta) {
      if (delta > 0) return "fas fa-arrow-up";
      if (delta < 0) return "fas fa-arrow-down";
      return "fas fa-minus";
    },
    trendBadgeClass(delta) {
      if (delta > 0) return "trend-badge--up";
      if (delta < 0) return "trend-badge--down";
      return "trend-badge--flat";
    },
    formatTrendDelta(delta) {
      if (delta > 0) return `+${delta}`;
      return String(delta);
    },
    normalizeQueryNumber(value) {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? null : parsed;
    },
    buildRouteQuery(overrides = {}) {
      const query = {};
      const companyId = this.normalizeQueryNumber(
        Object.prototype.hasOwnProperty.call(overrides, "company")
          ? overrides.company
          : this.effectiveCompanyId
      );
      const areaOverride = Object.prototype.hasOwnProperty.call(
        overrides,
        "area"
      )
        ? overrides.area
        : this.selectedAreaId;
      const areaId = this.normalizeQueryNumber(areaOverride);
      const statuses = Array.isArray(overrides.statuses)
        ? overrides.statuses
            .map((value) => this.normalizeQueryNumber(value))
            .filter(Boolean)
        : [];

      if (companyId) {
        query.company = String(companyId);
      }
      if (areaId) {
        query.area = String(areaId);
      }
      if (overrides.status) {
        query.status = String(overrides.status);
      } else if (statuses.length > 1) {
        query.statuses = statuses.join(",");
      }
      if (overrides.search) {
        query.search = overrides.search;
      }
      if (overrides.overdue) {
        query.overdue = "1";
      }

      return query;
    },
    openRoute(path, query = {}) {
      if (
        this.$route.path === path &&
        JSON.stringify(this.$route.query) === JSON.stringify(query)
      ) {
        return;
      }
      this.$router.push({ path, query });
    },
    openMetric(metricKey) {
      if (!this.canOpenModuleOverview) {
        return;
      }

      const metricRoutes = {
        requirements: {
          path: "/grc/requisitos",
        },
        risks: {
          path: "/grc/riscos",
          query: {
            statuses: [RISK_STATUS.OPEN, RISK_STATUS.IN_REVIEW],
          },
        },
        controls: {
          path: "/grc/controles",
          query: {
            statuses: [
              CONTROL_STATUS.NOT_CONTROLLED,
              CONTROL_STATUS.PARTIALLY_CONTROLLED,
            ],
          },
        },
        plans: {
          path: "/grc/planos-acao",
          query: {
            statuses: [
              ACTION_PLAN_STATUS.IN_PROGRESS,
              ACTION_PLAN_STATUS.WAITING_APPROVAL,
            ],
          },
        },
        overdue: {
          path: "/grc/planos-acao",
          query: {
            statuses: [
              ACTION_PLAN_STATUS.PLANNED,
              ACTION_PLAN_STATUS.IN_PROGRESS,
              ACTION_PLAN_STATUS.WAITING_APPROVAL,
            ],
            overdue: true,
          },
        },
      };

      const routeConfig = metricRoutes[metricKey];
      if (!routeConfig) {
        return;
      }

      this.openRoute(
        routeConfig.path,
        this.buildRouteQuery(routeConfig.query || {})
      );
    },
    openTopRisk(item) {
      if (!item) {
        return;
      }

      this.openRoute(
        "/grc/riscos",
        this.buildRouteQuery({
          company: item.company,
          area: item.area,
          search: item.title,
          statuses: this.activeRiskStatuses,
        })
      );
    },
    openPriorityPlan(item) {
      if (!item) {
        return;
      }

      this.openRoute(
        "/grc/planos-acao",
        this.buildRouteQuery({
          company: item.company,
          area: item.area?._id || item.area,
          status: item.status,
          search: item.title,
        })
      );
    },
    openRequirementHighlight(item) {
      if (!item) {
        return;
      }

      this.openRoute(
        "/grc/requisitos",
        this.buildRouteQuery({
          company: item.company,
          area: item.area?._id || item.area,
          status: item.status,
          search: item.title,
        })
      );
    },
    openCompanyBreakdown(item) {
      if (!item) {
        return;
      }

      this.openRoute(
        "/grc/requisitos",
        this.buildRouteQuery({ company: item.companyId, area: null })
      );
    },
    openAreaBreakdown(item) {
      if (!item) {
        return;
      }

      this.openRoute(
        "/grc/requisitos",
        this.buildRouteQuery({
          company: item.companyId,
          area: item.areaId,
        })
      );
    },
    openReviewAlert(item) {
      if (!item) {
        return;
      }

      this.openRoute(
        "/grc/riscos",
        this.buildRouteQuery({
          company: item.company,
          area: item.area,
          search: item.title,
          statuses: this.activeRiskStatuses,
        })
      );
    },
    treatmentStrategyLabel(value) {
      const labels = {
        avoid: "Evitar",
        reduce: "Mitigar",
        share: "Compartilhar",
        transfer: "Transferir",
        accept: "Aceitar",
      };
      return labels[value] || "-";
    },
    priorityLabel(value) {
      const labels = {
        low: "Baixa",
        medium: "Média",
        high: "Alta",
      };
      return labels[value] || "-";
    },
    priorityVariant(value) {
      const variants = {
        low: "secondary",
        medium: "warning",
        high: "danger",
      };
      return variants[value] || "secondary";
    },
    actionPlanStatusLabel(value) {
      return ACTION_PLAN_STATUS_META[value]?.label || "-";
    },
    actionPlanStatusVariant(value) {
      const variants = {
        [ACTION_PLAN_STATUS.PLANNED]: "secondary",
        [ACTION_PLAN_STATUS.IN_PROGRESS]: "info",
        [ACTION_PLAN_STATUS.COMPLETED]: "success",
        [ACTION_PLAN_STATUS.WAITING_APPROVAL]: "warning",
        [ACTION_PLAN_STATUS.SUSPENDED]: "danger",
      };
      return variants[value] || "secondary";
    },
    requirementStatusLabel(value) {
      return REQUIREMENT_STATUS_META[value]?.label || "-";
    },
    requirementStatusVariant(value) {
      const variants = {
        [REQUIREMENT_STATUS.NOT_ATTENDED]: "danger",
        [REQUIREMENT_STATUS.PARTIALLY_ATTENDED]: "warning",
        [REQUIREMENT_STATUS.ATTENDED]: "success",
        [REQUIREMENT_STATUS.NOT_APPLICABLE]: "secondary",
      };
      return variants[value] || "secondary";
    },
    riskLevelLabel(value) {
      const labels = {
        insignificant: "Insignificante",
        low: "Baixo",
        medium: "Médio",
        high: "Alto",
        catastrophic: "Catastrófico",
      };
      return labels[value] || "-";
    },
    riskLevelVariant(value) {
      const variants = {
        insignificant: "secondary",
        low: "secondary",
        medium: "warning",
        high: "danger",
        catastrophic: "dark",
      };
      return variants[value] || "secondary";
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      return new Date(value).toLocaleDateString("pt-BR");
    },
  },
};
</script>

<style scoped>
.grc-dashboard-module {
  min-height: 120px;
}

h4 {
  font-weight: 700;
  color: #0f172a;
}

.metrics-container {
  border: 0;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
  border-radius: 18px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.metric-section {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1rem 1.1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  display: flex;
  flex-direction: column;
}

.metric-section-clickable {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.metric-section-clickable:hover {
  transform: translateY(-2px);
  border-color: #93c5fd;
  box-shadow: 0 14px 24px rgba(59, 130, 246, 0.12);
}

.metric-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.metric-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #dbeafe;
  color: #1d4ed8;
}

.metric-icon--blue {
  background: #dbeafe;
  color: #1d4ed8;
}
.metric-icon--amber {
  background: #fef3c7;
  color: #b45309;
}
.metric-icon--rose {
  background: #ffe4e6;
  color: #be123c;
}
.metric-icon--violet {
  background: #ede9fe;
  color: #6d28d9;
}
.metric-icon--red {
  background: #fee2e2;
  color: #b91c1c;
}

.metric-label {
  margin: 0;
  color: #475569;
  font-size: 0.9rem;
}

.metric-value {
  margin: 0.9rem 0 0.35rem;
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
}

.metric-caption {
  color: #64748b;
  font-size: 0.82rem;
}

.metric-trend {
  margin-top: 0.6rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
}

.trend-badge--up {
  background: #fee2e2;
  color: #b91c1c;
}
.trend-badge--down {
  background: #dcfce7;
  color: #166534;
}
.trend-badge--flat {
  background: #e2e8f0;
  color: #475569;
}

.trend-caption {
  font-size: 0.72rem;
  color: #64748b;
}

.metric-alert {
  margin-top: 0.5rem;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.75rem;
  padding: 0.35rem 0.5rem;
  border-radius: 8px;
  border-left: 3px solid #ef4444;
}

.dashboard-card {
  border: 0;
  border-radius: 18px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
}

.card-title {
  font-weight: 700;
  color: #0f172a;
}

.donut-title {
  font-size: 0.92rem;
  font-weight: 600;
}

.quick-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.gap-2 {
  gap: 0.5rem;
}

.grc-table {
  font-size: 0.92rem;
}

.grc-area-filter {
  max-width: 240px;
}
</style>

<style>
.grc-row-overdue td {
  background-color: #fff5f5 !important;
}
</style>
